import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#services", label: "Services" },
  { href: "#work", label: "Work" },
  { href: "#process", label: "Process" },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div
          className={`flex items-center justify-between brut-border bg-[#FDFBF7] px-4 sm:px-6 py-3 transition-shadow ${
            scrolled ? "brut-shadow-sm" : "brut-shadow"
          }`}
        >
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={(e) => handleClick(e, "#top")}
            className="flex items-center gap-2 group"
          >
            <span className="inline-flex items-center justify-center w-8 h-8 bg-[#FF0055] text-white brut-border font-display font-black text-sm group-hover:rotate-6 transition-transform">
              I!
            </span>
            <span className="font-display font-black uppercase text-base tracking-tight">
              IncInk<span className="text-[#FF0055]">.</span>Creative
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => handleClick(e, l.href)}
                className="px-3 py-2 font-display font-bold text-sm uppercase tracking-wider hover:bg-[#FFD800] brut-border border-transparent hover:border-[#0D0D0D] transition-all"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta"
              onClick={(e) => handleClick(e, "#contact")}
              className="ml-3 btn-primary btn-cyan !py-2.5 !px-4 text-xs"
            >
              Start a Project
            </a>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden brut-border w-10 h-10 flex items-center justify-center bg-[#FFD800]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-3 brut-border bg-white brut-shadow p-4 flex flex-col gap-1"
              data-testid="nav-mobile-menu"
            >
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => handleClick(e, l.href)}
                  className="px-3 py-3 font-display font-bold uppercase text-sm hover:bg-[#FFD800] brut-border border-transparent hover:border-[#0D0D0D]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => handleClick(e, "#contact")}
                className="mt-2 btn-primary btn-magenta justify-center"
              >
                Start a Project
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
